/**
 * Servicio para gestión de solicitudes de unión a clubes.
 *
 * Aprobar y rechazar NO viven aquí: son las Cloud Functions
 * `approveJoinRequest` / `rejectJoinRequest` (crean Person + Member y asignan
 * la dirección de sección en una transacción, y son las únicas que pueden
 * hacerlo — las rules ya no dejan que el cliente toque el status salvo para
 * cancelar la propia solicitud).
 */

import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  addDoc,
  Timestamp,
  limit,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { ClubType } from "@/types";
import type { EncryptionMeta } from "./crypto";

// ============================================
// TIPOS
// ============================================

export type JoinRequestStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled";

export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
}

export interface JoinRequest {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  userPhotoUrl?: string;
  clubId: string;
  clubName: string;
  clubType: string;

  // Información personal del solicitante
  firstName?: string;
  lastName?: string;
  phone?: string;
  birthDate?: Date;
  idDocumentType?: string;
  idDocumentNumber?: string;
  gender?: string;
  bloodType?: string;
  address?: string;
  healthInsurance?: string;
  emergencyContact?: EmergencyContact;
  memberType?: string;

  /**
   * Si true, la solicitud es de un ACUDIENTE registrando a un DEPENDIENTE
   * (su hijo) — los datos personales son del hijo, y `userId` es la cuenta del
   * acudiente. Al aprobar, la persona se crea como ward (`guardianUserId`).
   */
  forWard?: boolean;
  /** Relación del acudiente con el dependiente (padre/madre/tutor…). */
  guardianRelation?: string;
  /** Nombre del acudiente (para mostrar y denormalizar en el ward). */
  guardianName?: string;

  /**
   * Rol que pide el solicitante. Ausente o "member" = ingreso normal.
   * "section_director" = dice ser director/a de una sección de un club que YA
   * está registrado. Al aprobar, la Cloud Function además de crear la
   * membresía le asigna la dirección de esa sección (activándola si hacía
   * falta). Es lo que evita que el director de una sección nueva registre un
   * club duplicado solo para poder entrar.
   */
  requestedRole?: "member" | "section_director";
  /** Sección que solicita dirigir (solo con requestedRole="section_director"). */
  requestedSection?: ClubType;
  /** Cargo declarado por el solicitante (director, subdirector, consejero…). */
  requestedRoleNote?: string;

  message?: string;
  invitationCode?: string;
  status: JoinRequestStatus;
  rejectionReason?: string;
  processedBy?: string;
  processedAt?: Date;

  // ============================================
  // Documentos personales (subidos con la solicitud)
  // ============================================
  // Foto tipo carnet — NO encriptada (es la imagen pública del carnet).
  photoUrl?: string;
  photoPath?: string;
  // Documento de identidad — anverso (encriptado con AES-256-GCM).
  documentFrontUrl?: string;
  documentFrontPath?: string;
  documentFrontEncryption?: EncryptionMeta;
  documentFrontContentType?: string;
  // Documento de identidad — reverso (encriptado).
  documentBackUrl?: string;
  documentBackPath?: string;
  documentBackEncryption?: EncryptionMeta;
  documentBackContentType?: string;
  // Soporte de EPS — opcional (encriptado).
  epsDocumentUrl?: string;
  epsDocumentPath?: string;
  epsDocumentEncryption?: EncryptionMeta;
  epsDocumentContentType?: string;

  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// SERVICIO
// ============================================

const COLLECTION = "joinRequests";

/**
 * Obtiene las solicitudes pendientes de un club.
 */
export async function getClubPendingRequests(
  clubId: string,
): Promise<JoinRequest[]> {
  const q = query(
    collection(db, COLLECTION),
    where("clubId", "==", clubId),
    where("status", "==", "pending"),
    orderBy("createdAt", "asc"),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    birthDate: (doc.data().birthDate as Timestamp)?.toDate(),
    createdAt: (doc.data().createdAt as Timestamp)?.toDate() || new Date(),
    updatedAt: (doc.data().updatedAt as Timestamp)?.toDate() || new Date(),
    processedAt: (doc.data().processedAt as Timestamp)?.toDate(),
  })) as JoinRequest[];
}

/**
 * Stream de solicitudes pendientes de un club.
 */
export function watchClubPendingRequests(
  clubId: string,
  onData: (requests: JoinRequest[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where("clubId", "==", clubId),
    where("status", "==", "pending"),
    orderBy("createdAt", "asc"),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const requests = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        birthDate: (doc.data().birthDate as Timestamp)?.toDate(),
        createdAt: (doc.data().createdAt as Timestamp)?.toDate() || new Date(),
        updatedAt: (doc.data().updatedAt as Timestamp)?.toDate() || new Date(),
        processedAt: (doc.data().processedAt as Timestamp)?.toDate(),
      })) as JoinRequest[];
      onData(requests);
    },
    onError,
  );
}

/**
 * Stream de las solicitudes PENDIENTES que piden DIRIGIR un tipo de club, de
 * todos los clubes. Es la vista del admin de asociación: nombrar un director
 * no debería enterarse solo el club afectado.
 *
 * El recorte por asociación se hace en la capa de aplicación (la solicitud no
 * guarda associationId; se resuelve por su club), igual que el resto del
 * scoping fino del proyecto.
 */
export function watchPendingSectionDirectorRequests(
  onData: (requests: JoinRequest[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where("requestedRole", "==", "section_director"),
    where("status", "==", "pending"),
    orderBy("createdAt", "asc"),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const requests = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        birthDate: (doc.data().birthDate as Timestamp)?.toDate(),
        createdAt: (doc.data().createdAt as Timestamp)?.toDate() || new Date(),
        updatedAt: (doc.data().updatedAt as Timestamp)?.toDate() || new Date(),
        processedAt: (doc.data().processedAt as Timestamp)?.toDate(),
      })) as JoinRequest[];
      onData(requests);
    },
    onError,
  );
}

/**
 * Obtiene todas las solicitudes de un club (historial).
 */
export async function getClubRequestHistory(
  clubId: string,
): Promise<JoinRequest[]> {
  const q = query(
    collection(db, COLLECTION),
    where("clubId", "==", clubId),
    orderBy("createdAt", "desc"),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    createdAt: (doc.data().createdAt as Timestamp)?.toDate() || new Date(),
    updatedAt: (doc.data().updatedAt as Timestamp)?.toDate() || new Date(),
    processedAt: (doc.data().processedAt as Timestamp)?.toDate(),
  })) as JoinRequest[];
}

/**
 * Crea una solicitud de unión.
 * Valida que no exista ya una solicitud pendiente del mismo usuario para el mismo club.
 */
export async function createJoinRequest(
  data: Omit<JoinRequest, "id" | "createdAt" | "updatedAt">,
): Promise<string> {
  // B1: Validar duplicado — no permitir solicitud pendiente duplicada.
  // Para acudientes (forWard) el dedup es por DOCUMENTO del dependiente: un
  // padre SÍ puede tener varias solicitudes (un hijo c/u) al mismo club, pero
  // no el mismo documento dos veces.
  if (data.forWard) {
    if (data.idDocumentNumber) {
      const dupWardSnap = await getDocs(
        query(
          collection(db, COLLECTION),
          where("userId", "==", data.userId),
          where("clubId", "==", data.clubId),
          where("idDocumentNumber", "==", data.idDocumentNumber),
          where("status", "==", "pending"),
          limit(1),
        ),
      );
      if (!dupWardSnap.empty) {
        throw new Error(
          "Ya tienes una solicitud pendiente para esta persona en este club",
        );
      }

      // B1b: si ya existe una ficha con ese documento y es miembro ACTIVO de
      // este club, no crear otra solicitud (el dedup por solicitudes pendientes
      // no cubre a los ya aprobados). Una ficha existente SIN membresía activa
      // sí puede volver a solicitarse (reingreso): la aprobación la reutiliza.
      try {
        const { personService } = await import("@/lib/services/persons");
        const existing = await personService.findReusableByDocument(
          data.idDocumentType ?? "",
          data.idDocumentNumber,
        );
        if (existing?.id) {
          const memberSnap = await getDocs(
            query(
              collection(db, "members"),
              where("personId", "==", existing.id),
              where("clubId", "==", data.clubId),
              where("status", "==", "active"),
              limit(1),
            ),
          );
          if (!memberSnap.empty) {
            throw new Error(
              `${existing.fullName ?? "Esta persona"} ya es miembro activo de este club — no necesitas volver a registrarla.`,
            );
          }
        }
      } catch (e) {
        if (e instanceof Error && e.message.includes("ya es miembro activo")) {
          throw e;
        }
        // Error de permisos/red al consultar: no bloquear la solicitud.
      }
    }
  } else {
    const existingSnap = await getDocs(
      query(
        collection(db, COLLECTION),
        where("userId", "==", data.userId),
        where("clubId", "==", data.clubId),
        where("status", "==", "pending"),
        limit(1),
      ),
    );
    if (!existingSnap.empty) {
      throw new Error("Ya tienes una solicitud pendiente para este club");
    }

    // F12-QW4: el dedup de arriba solo mira PENDING, así que una cuenta
    // atrapada en el loop del wizard (aprobada pero sin ver su club porque la
    // ficha quedó en otra cuenta) mandaba una solicitud nueva cada vez, y el
    // director volvía a aprobarla — hay 29 duplicadas así en producción. Si ya
    // fue aprobada, el problema NO es la solicitud: es el acceso a la ficha.
    // El mensaje lo dice para que el usuario pida lo correcto.
    // EXCEPCIONES a esta guarda:
    //  - `section_director`: ya soy miembro aprobado y ahora pido un cargo.
    //  - Registrar a OTRA persona (documento distinto al de la ficha de la
    //    sesión): no es una re-solicitud propia. Caso real (Mario, GAMA,
    //    2026-08-06): un papá usó este formulario para su hija y recibió
    //    "tu solicitud ya fue aprobada", que no tenía nada que ver. Se le
    //    responde con el camino correcto.
    let ownDocument: string | undefined;
    try {
      const linked = await getDocs(
        query(
          collection(db, "persons"),
          where("linkedUserId", "==", data.userId),
          limit(1),
        ),
      );
      ownDocument = linked.empty
        ? undefined
        : (linked.docs[0].data() as { idDocumentNumber?: string })
            .idDocumentNumber;
    } catch {
      // Sin permisos/red: seguir con la guarda normal.
    }
    const registeringSomeoneElse =
      !!ownDocument &&
      !!data.idDocumentNumber &&
      ownDocument.trim() !== data.idDocumentNumber.trim();
    if (registeringSomeoneElse) {
      throw new Error(
        `Este formulario es para registrarte a ti. Para inscribir a ${
          data.firstName ?? "otra persona"
        }, ve a tu Perfil → pestaña "A mi cargo" → "Registrar a una persona a mi cargo".`,
      );
    }

    const approvedSnap =
      data.requestedRole === "section_director"
        ? { empty: true }
        : await getDocs(
            query(
              collection(db, COLLECTION),
              where("userId", "==", data.userId),
              where("clubId", "==", data.clubId),
              where("status", "==", "approved"),
              limit(1),
            ),
          );
    if (!approvedSnap.empty) {
      throw new Error(
        "Tu solicitud para este club YA fue aprobada. Si no ves el club al " +
          "entrar, es un problema de acceso a tu ficha (suele estar asociada " +
          "a un correo anterior): escríbele al director de tu club o a soporte " +
          "para trasladarla — no necesitas registrarte de nuevo.",
      );
    }
  }

  // B2: Detectar suplantación accidental — si el usuario que envía la solicitud
  // ya está vinculado a una Person con datos distintos a los del formulario,
  // significa que están reusando una sesión de otra persona (caso típico:
  // el director comparte el link y lo abre en su mismo navegador).
  // Bloqueamos para evitar crear "solicitudes fantasma" que al aprobar no
  // crean a nadie nuevo.
  // NO aplica a acudientes (forWard): ahí es ESPERADO que los datos del
  // formulario (el hijo) difieran de la persona vinculada a la sesión (el padre).
  if (!data.forWard && (data.idDocumentNumber || data.firstName)) {
    try {
      const linkedPersonsQ = query(
        collection(db, "persons"),
        where("linkedUserId", "==", data.userId),
        limit(1),
      );
      const linkedSnap = await getDocs(linkedPersonsQ);
      if (!linkedSnap.empty) {
        const p = linkedSnap.docs[0].data() as {
          idDocumentNumber?: string;
          firstName?: string;
          lastName?: string;
          fullName?: string;
        };
        const provDoc = (data.idDocumentNumber || "").trim();
        const personDoc = (p.idDocumentNumber || "").trim();
        const provFirst = (data.firstName || "").trim().toLowerCase();
        const personFirst = (p.firstName || "").trim().toLowerCase();
        const provLast = (data.lastName || "").trim().toLowerCase();
        const personLast = (p.lastName || "").trim().toLowerCase();
        const docMismatch = provDoc && personDoc && provDoc !== personDoc;
        const nameMismatch =
          (provFirst && personFirst && provFirst !== personFirst) ||
          (provLast && personLast && provLast !== personLast);
        if (docMismatch || nameMismatch) {
          throw new Error(
            `Estás registrando a "${`${data.firstName ?? ""} ${data.lastName ?? ""}`.trim()}" pero esta sesión ya pertenece a "${p.fullName ?? p.firstName ?? "otro usuario"}". Cierra sesión o abre el enlace en una ventana de incógnito para registrar a otra persona.`,
          );
        }
      }
    } catch (e) {
      // Si es nuestro error de validación, propagarlo. Si es un error de
      // permisos/red al consultar persons, no bloquear la solicitud.
      if (e instanceof Error && e.message.startsWith("Estás registrando a")) {
        throw e;
      }
    }
  }

  const docRef = await addDoc(collection(db, COLLECTION), {
    ...data,
    status: "pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Obtiene la solicitud pendiente de un usuario (si existe).
 */
export async function getUserPendingRequest(
  userId: string,
): Promise<JoinRequest | null> {
  const q = query(
    collection(db, COLLECTION),
    where("userId", "==", userId),
    where("status", "==", "pending"),
    limit(1),
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const d = snapshot.docs[0];
  return {
    id: d.id,
    ...d.data(),
    birthDate: (d.data().birthDate as Timestamp)?.toDate(),
    createdAt: (d.data().createdAt as Timestamp)?.toDate() || new Date(),
    updatedAt: (d.data().updatedAt as Timestamp)?.toDate() || new Date(),
    processedAt: (d.data().processedAt as Timestamp)?.toDate(),
  } as JoinRequest;
}

/**
 * Obtiene la solicitud más reciente del usuario en estado pendiente o rechazado.
 * Útil para decidir redirección de sesión sin perder el caso rejected.
 */
/**
 * F12-13: nombramientos de dirección que FALLARON al aprobar.
 *
 * La solicitud queda "approved" (la membresía sí se creó) pero la persona NO
 * quedó como directora — antes eso solo se veía en un banner efímero y se
 * perdía. Caso real: Keila (JAHDAI) pidió dirigir Aventureros, falló porque su
 * ficha estaba en otra cuenta, y nadie se enteró hasta reconstruirlo a mano.
 */
export async function getFailedDirectorAssignments(): Promise<JoinRequest[]> {
  const snapshot = await getDocs(
    query(
      collection(db, COLLECTION),
      where("requestedRole", "==", "section_director"),
      where("status", "==", "approved"),
      limit(100),
    ),
  );
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }) as JoinRequest & {
      sectionDirectorOutcome?: { assigned?: boolean; reason?: string };
    })
    .filter((r) => r.sectionDirectorOutcome?.assigned === false);
}

/**
 * F12-P1: última solicitud APROBADA del usuario.
 *
 * `getUserLatestRequest` solo devuelve pending|rejected (las "accionables"),
 * así que una cuenta cuya solicitud fue aprobada pero que NO ve su membresía
 * —porque la ficha quedó vinculada a un correo anterior— caía en
 * `needsSetup = true` y el wizard se le repetía indefinidamente (casos Julie,
 * Daniela, Keila). Esto permite detectar ese estado y explicarlo en vez de
 * volver a pedirle los datos.
 */
export async function getUserApprovedRequest(
  userId: string,
): Promise<JoinRequest | null> {
  const snapshot = await getDocs(
    query(
      collection(db, COLLECTION),
      where("userId", "==", userId),
      where("status", "==", "approved"),
      limit(10),
    ),
  );
  if (snapshot.empty) return null;
  const docs = snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Record<string, unknown>)
    .sort((a, b) => {
      const ms = (x: Record<string, unknown>) =>
        (x.processedAt as Timestamp)?.toDate?.()?.getTime?.() ??
        (x.createdAt as Timestamp)?.toDate?.()?.getTime?.() ??
        0;
      return ms(b) - ms(a);
    });
  const d = docs[0];
  return {
    ...d,
    birthDate: (d.birthDate as Timestamp)?.toDate?.(),
    createdAt: (d.createdAt as Timestamp)?.toDate?.() || new Date(),
    updatedAt: (d.updatedAt as Timestamp)?.toDate?.() || new Date(),
    processedAt: (d.processedAt as Timestamp)?.toDate?.(),
  } as unknown as JoinRequest;
}

export async function getUserLatestRequest(
  userId: string,
): Promise<JoinRequest | null> {
  const q = query(
    collection(db, COLLECTION),
    where("userId", "==", userId),
    limit(50),
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const actionable = snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((d: any) => d.status === "pending" || d.status === "rejected")
    .sort((a: any, b: any) => {
      const aMs = a.createdAt?.toDate?.()?.getTime?.() || 0;
      const bMs = b.createdAt?.toDate?.()?.getTime?.() || 0;
      return bMs - aMs;
    });

  if (actionable.length === 0) return null;
  const d: any = actionable[0];
  return {
    id: d.id,
    ...d,
    birthDate: (d.birthDate as Timestamp)?.toDate?.(),
    createdAt: (d.createdAt as Timestamp)?.toDate?.() || new Date(),
    updatedAt: (d.updatedAt as Timestamp)?.toDate?.() || new Date(),
    processedAt: (d.processedAt as Timestamp)?.toDate?.(),
  } as JoinRequest;
}

/**
 * Stream de la solicitud pendiente de un usuario.
 */
export function watchUserPendingRequest(
  userId: string,
  onData: (request: JoinRequest | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where("userId", "==", userId),
    where("status", "==", "pending"),
    limit(1),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        onData(null);
        return;
      }
      const d = snapshot.docs[0];
      onData({
        id: d.id,
        ...d.data(),
        birthDate: (d.data().birthDate as Timestamp)?.toDate(),
        createdAt: (d.data().createdAt as Timestamp)?.toDate() || new Date(),
        updatedAt: (d.data().updatedAt as Timestamp)?.toDate() || new Date(),
        processedAt: (d.data().processedAt as Timestamp)?.toDate(),
      } as JoinRequest);
    },
    onError,
  );
}

/**
 * Stream de la solicitud más reciente de un usuario (cualquier estado).
 * Uso: PendingApprovalPage — permite detectar rechazos sin loop infinito.
 */
export function watchUserLatestRequest(
  userId: string,
  onData: (request: JoinRequest | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where("userId", "==", userId),
    limit(50),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        onData(null);
        return;
      }

      const actionable = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d: any) => d.status === "pending" || d.status === "rejected")
        .sort((a: any, b: any) => {
          const aMs = a.createdAt?.toDate?.()?.getTime?.() || 0;
          const bMs = b.createdAt?.toDate?.()?.getTime?.() || 0;
          return bMs - aMs;
        });

      if (actionable.length === 0) {
        onData(null);
        return;
      }

      const d: any = actionable[0];
      onData({
        id: d.id,
        ...d,
        birthDate: (d.birthDate as Timestamp)?.toDate?.(),
        createdAt: (d.createdAt as Timestamp)?.toDate?.() || new Date(),
        updatedAt: (d.updatedAt as Timestamp)?.toDate?.() || new Date(),
        processedAt: (d.processedAt as Timestamp)?.toDate?.(),
      } as JoinRequest);
    },
    onError,
  );
}

/**
 * Cancela una solicitud pendiente.
 */
export async function cancelRequest(requestId: string): Promise<void> {
  const docRef = doc(db, COLLECTION, requestId);
  await updateDoc(docRef, {
    status: "cancelled",
    updatedAt: serverTimestamp(),
  });
}

export const joinRequestService = {
  getClubPendingRequests,
  watchClubPendingRequests,
  watchPendingSectionDirectorRequests,
  getClubRequestHistory,
  createJoinRequest,
  getUserPendingRequest,
  getUserLatestRequest,
  getUserApprovedRequest,
  getFailedDirectorAssignments,
  watchUserPendingRequest,
  watchUserLatestRequest,
  cancelRequest,
};
